import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/store/appstate';
import { selectCompanyByCompanyCode } from 'src/store/entityStores/company-selectors';
import { TcsCustomPermission } from 'src/app/shared/authorization/permissions';
import { UserService } from '../tcs-identity/user.service';
import { OpsAuthorizationService } from './ops-authorization.service';

@Injectable({ providedIn: 'root' })
export class InsightsAuthorizationService {
  constructor(
    private _opsAuthz: OpsAuthorizationService,
    private _user: UserService,
    private _store: Store<AppState>,
  ) {}

  public isAuthorizedForCompanyCode(companyCode: string) {
    return this._store
      .select(selectCompanyByCompanyCode(companyCode))
      .pipe(switchMap((c) => (c != null ? this.isAuthorizedForCompanyId(c.id) : of(false))));
  }

  public isAuthorizedForCompanyId(companyId: string) {
    const hasGlobalRoleWithInsightsAccess$ = this._user.hasAccessibleCompany(companyId).pipe(
      withLatestFrom(this._user.hasPermissionAnyGlobalRole(TcsCustomPermission.AccessInsights)),
      map(
        ([hasCompanyAccess, hasGlobalRoleOrgAccess]) => hasCompanyAccess && hasGlobalRoleOrgAccess,
      ),
    );
    return combineLatest([
      hasGlobalRoleWithInsightsAccess$,
      this._opsAuthz.isAuthorizedForCompanyId(companyId),
      this._user.hasPermissionAnyScope(TcsCustomPermission.AccessInsights),
    ]).pipe(
      map(([hasGlobalRoleWithInsightsAccess, hasOpsAccess, hasInsightsAccess]) => {
        // If a user has AccessInsights permission for any global roles (user.userRoles) and access to the company OR
        // If a user has AccessInsights permission for any scope (user.userRolesAllScopes) and has access to ops for the company
        // then they are authorized
        return (hasInsightsAccess && hasOpsAccess) || hasGlobalRoleWithInsightsAccess;
      }),
      distinctUntilChanged(),
    );
  }
}
